import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';

const fmtPaceMinPerKm = (seconds, km) => {
  const s = Number(seconds);
  const d = Number(km);
  if (!Number.isFinite(s) || !Number.isFinite(d) || d <= 0) return '—';
  const pace = s / d;
  const m = Math.floor(pace / 60);
  const sec = Math.round(pace - m * 60);
  return `${m}:${String(sec).padStart(2, '0')}/km`;
};

const fmtDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.round(s - m * 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const normalizeRows = (debrief) => {
  const laps = Array.isArray(debrief?.laps) ? debrief.laps : [];
  if (laps.length > 0) {
    return {
      kind: 'laps',
      rows: laps.map((l) => {
        const km = l?.distance ? Number(l.distance) / 1000 : null;
        const sec = Number(l?.elapsed_time);
        return {
          idx: l?.lap ?? null,
          km: km != null && Number.isFinite(km) ? Math.round(km * 100) / 100 : null,
          seconds: Number.isFinite(sec) ? sec : null,
          avg_hr: l?.avg_hr != null ? Math.round(Number(l.avg_hr)) : null,
          elev_gain_m: l?.total_elevation_gain != null ? Math.round(Number(l.total_elevation_gain)) : null
        };
      })
    };
  }

  const splits = Array.isArray(debrief?.splits) ? debrief.splits : [];
  if (splits.length > 0) {
    return {
      kind: 'splits',
      rows: splits.map((s) => ({
        idx: s?.km ?? s?.split ?? null,
        km: 1,
        seconds: s?.time_seconds ?? s?.elapsed_time ?? null,
        avg_hr: s?.avg_hr != null ? Math.round(Number(s.avg_hr)) : null,
        elev_gain_m: s?.elevation_gain ?? s?.elevation_gain_m ?? null
      }))
    };
  }

  return { kind: 'none', rows: [] };
};

export default function ExecutedSplitsOrLapsTable({ debrief }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const data = useMemo(() => normalizeRows(debrief), [debrief]);
  if (data.kind === 'none') return null;

  const title = data.kind === 'laps' ? t('workout.laps') : t('workout.splits');
  const count = data.rows.length;

  return (
    <div className="bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{title}</div>
          <div className="bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-0.5 rounded-full">{count}</div>
        </div>
        <div className="text-gray-400">
          {open ? <HiChevronDown className="w-5 h-5" /> : <HiChevronRight className="w-5 h-5" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50/50">
              <tr className="text-[10px] uppercase tracking-widest text-gray-400 font-black">
                <th className="text-left p-4 pl-6">{t('workout.segment')}</th>
                <th className="text-right p-4">{t('workout.distance')}</th>
                <th className="text-right p-4">{t('workout.duration')}</th>
                <th className="text-right p-4">{t('workout.pace')}</th>
                <th className="text-right p-4">{t('workout.avg_hr')}</th>
                <th className="text-right p-4 pr-6">{t('workout.elevation')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.rows.slice(0, 60).map((r, i) => {
                const km = r.km;
                const seconds = r.seconds;
                return (
                  <tr key={i} className="hover:bg-gray-50/30 transition-colors">
                    <td className="p-4 pl-6 font-bold font-mono text-gray-900">{r.idx ?? i + 1}</td>
                    <td className="p-4 text-right tabular-nums text-gray-600">{km != null ? `${km}` : '—'} <span className="text-gray-400 text-xs">km</span></td>
                    <td className="p-4 text-right tabular-nums text-gray-600">{seconds != null ? fmtDuration(seconds) : '—'}</td>
                    <td className="p-4 text-right tabular-nums font-bold text-gray-900">
                      {seconds != null && km != null ? fmtPaceMinPerKm(seconds, km) : '—'}
                    </td>
                    <td className="p-4 text-right tabular-nums text-gray-600">{r.avg_hr != null ? `${r.avg_hr}` : '—'}</td>
                    <td className="p-4 pr-6 text-right tabular-nums text-gray-600">
                      {r.elev_gain_m != null ? `+${r.elev_gain_m}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


